<template>
  <transition name="upload-video-wizard" appear>
    <div
      v-if="value"
      class="upload-video-wizard__overlay"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="dialogLabelledBy"
      @mousedown.self="onClose"
    >
      <div class="uvw">
        <div class="uvw__header">
          <div class="uvw__featured-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                opacity="0.4"
                d="M20 16.7428C21.2215 15.734 22 14.2079 22 12.5C22 9.46243 19.5376 7 16.5 7C16.2815 7 16.0771 6.886 15.9661 6.69774C14.6621 4.48484 12.2544 3 9.5 3C5.35786 3 2 6.35786 2 10.5C2 12.5661 2.83545 14.4371 4.18695 15.7935"
                stroke="black"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M16 16L12 12L8 16M12 12L12 21"
                stroke="black"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <span v-if="displayTitle" id="upload-video-wizard-title" class="uvw__title">
            {{ displayTitle }}
          </span>

          <button type="button" class="uvw__close" aria-label="Close" @click="onClose">
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="uvw__content">
          <!-- Upload state (empty dropzone) -->
          <div
            v-if="currentState === 'upload'"
            class="uvw__dropzone"
            :class="{ 'uvw__dropzone--drag': isDragging }"
            role="button"
            tabindex="0"
            @click="openFilePicker"
            @keydown.enter.prevent="openFilePicker"
            @keydown.space.prevent="openFilePicker"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
          >
            <div class="uvw__dropzone-icon">
              <img
                :src="require('@components/assets/upload-video.svg')"
                alt=""
                class="uvw__dropzone-icon-img"
              />
            </div>
            <div class="uvw__dropzone-text">
              <span class="uvw__dropzone-action">Click to upload</span>
              <span class="uvw__dropzone-or">&nbsp;or drag and drop</span>
            </div>
            <span class="uvw__dropzone-help">Most video formats (MP4 recommended)</span>
          </div>

          <!-- Selected / Progress state (file row) -->
          <div
            v-else-if="currentState === 'selected' || currentState === 'progress'"
            class="uvw__file-row"
          >
            <div class="uvw__file-icon" aria-hidden="true">
              <img
                :src="require('@components/assets/file-video.svg')"
                alt=""
                class="uvw__file-icon-img"
              />
              <span class="uvw__file-badge">MP4</span>
            </div>

            <div class="uvw__file-info">
              <span class="uvw__file-name" :title="displayFileName">{{ displayFileName }}</span>
              <span class="uvw__file-size">{{ displayFileSize }}</span>
              <div v-if="currentState === 'progress'" class="uvw__progress">
                <div class="uvw__progress-track">
                  <div class="uvw__progress-fill" :style="{ width: clampedProgress + '%' }"></div>
                </div>
                <span class="uvw__progress-text">{{ clampedProgress }}%</span>
              </div>
            </div>

            <button type="button" class="uvw__trash" aria-label="Remove file" @click="onRemove">
              <img
                :src="require('@components/assets/trash-01.svg')"
                alt=""
                class="uvw__trash-icon"
              />
            </button>
          </div>

          <!-- Processing (Cloudflare Stream status) -->
          <div v-else-if="currentState === 'processing'" class="uvw__processing" role="status">
            <StreamProcessingLoaderSvg class="uvw__processing-loader" />
            <span class="uvw__processing-text">{{ processingText }}</span>
          </div>

          <!-- Validating (video_to_components / IQ context check) -->
          <div v-else-if="currentState === 'validating'" class="uvw__thinking" role="status">
            <CheckpointIQGenerationStatus
              :status="iqGenerationStatus"
              :trace-entries="iqTraceEntries"
              :in-progress="iqGenerationInProgress"
              :show-status-pills="showIqStatusPills"
              activity-label="Watching the video…"
            />
          </div>

          <!-- Match (task page — context validation PASS) -->
          <div
            v-else-if="currentState === 'related'"
            class="uvw__status-alert uvw__status-alert--success"
            role="status"
          >
            <div class="uvw__status-alert-icon uvw__status-alert-icon--success" aria-hidden="true">
              <span class="uvw__status-alert-ring uvw__status-alert-ring--outer" />
              <span class="uvw__status-alert-ring uvw__status-alert-ring--inner" />
              <img
                :src="require('@components/assets/check-circle-success.svg')"
                alt=""
                class="uvw__status-alert-icon-img"
              />
            </div>
            <div class="uvw__status-alert-body">
              <p id="upload-video-wizard-alert-title" class="uvw__status-alert-title">
                {{ relatedTitle }}
              </p>
              <p class="uvw__status-alert-text">{{ relatedText }}</p>
            </div>
          </div>

          <!-- Mismatch (task page — context validation FAIL) -->
          <div
            v-else-if="currentState === 'not-related'"
            class="uvw__status-alert uvw__status-alert--error"
            role="alert"
          >
            <div class="uvw__status-alert-icon uvw__status-alert-icon--error" aria-hidden="true">
              <span class="uvw__status-alert-ring uvw__status-alert-ring--outer" />
              <span class="uvw__status-alert-ring uvw__status-alert-ring--inner" />
              <img
                :src="require('@components/assets/alert-circle-error.svg')"
                alt=""
                class="uvw__status-alert-icon-img"
              />
            </div>
            <div class="uvw__status-alert-body">
              <p class="uvw__status-alert-title">{{ alertTitle }}</p>
              <p class="uvw__status-alert-text">{{ alertText }}</p>
            </div>
          </div>

          <!-- Error / Too Long -->
          <div v-else class="uvw__alert">
            <div class="uvw__alert-icon">
              <Icon name="alert" />
            </div>
            <span class="uvw__alert-title">{{ alertTitle }}</span>
            <span class="uvw__alert-text">{{ alertText }}</span>
          </div>
        </div>

        <div v-if="showAbort" class="uvw__actions uvw__actions--stretch">
          <StandardButton
            type="red"
            variant="primary"
            size="full-width"
            label="Abort"
            @click="onAbort"
          />
        </div>
        <div v-else-if="currentState === 'related'" class="uvw__actions uvw__actions--stretch">
          <StandardButton
            type="magenta"
            variant="primary"
            size="normal"
            :label="primaryLabel"
            @click="onSubmit"
          />
        </div>
        <div v-else-if="currentState === 'not-related'" class="uvw__actions uvw__actions--equal">
          <StandardButton
            type="magenta"
            variant="outline"
            size="normal"
            label="Discard Upload"
            @click="onClose"
          />
          <StandardButton
            type="magenta"
            variant="primary"
            size="normal"
            label="Upload a new video"
            @click="onSubmit"
          />
        </div>
        <div
          v-else-if="showActions"
          class="uvw__actions"
          :class="{ 'uvw__actions--equal': equalActions }"
        >
          <StandardButton
            v-if="showGoBack"
            type="magenta"
            variant="outline"
            size="normal"
            :label="cancelLabel"
            @click="onGoBack"
          />
          <StandardButton
            type="magenta"
            variant="primary"
            size="normal"
            :label="primaryLabel"
            :disabled="primaryDisabled"
            @click="onSubmit"
          />
        </div>

        <input
          ref="fileInput"
          type="file"
          :accept="accept"
          class="uvw__file-input"
          @change="onFileChange"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import Icon from "@components/icons/Icon.vue";
import StreamProcessingLoaderSvg from "@components/StreamProcessingLoaderSvg/StreamProcessingLoaderSvg.vue";
import CheckpointIQGenerationStatus from "@components/CheckpointIQGenerationStatus/CheckpointIQGenerationStatus.vue";

const MAX_DURATION_SECONDS = 300;

const props = defineProps({
  value: { type: Boolean, default: true },
  state: {
    type: String,
    default: "upload",
    validator: (v) =>
      [
        "upload",
        "selected",
        "progress",
        "error",
        "too-long",
        "processing",
        "validating",
        "related",
        "not-related",
      ].includes(v),
  },
  file: { type: File, default: null },
  progress: { type: Number, default: 0 },
  accept: { type: String, default: "video/*" },
  /** Dialog title — create flow keeps the default; replace/edit can override. */
  title: { type: String, default: "Upload Video and Create Checkpoint" },
  /** Primary CTA when a valid file is selected. */
  submitLabel: { type: String, default: "Upload and Create Checkpoint" },
  /** Primary CTA after error / too-long. */
  retryLabel: { type: String, default: "Upload again and create checkpoint" },
  /** Secondary CTA (checkpoint create keeps Go Back; tasks page uses Discard). */
  cancelLabel: { type: String, default: "Go Back" },
  /** Stretch Discard + Upload to equal width (tasks page only). */
  equalActions: { type: Boolean, default: false },
  /** Primary CTA after context-validation PASS (task page). */
  doneLabel: { type: String, default: "Close" },
  /** Optional supporting copy under the related / not-related titles. */
  resultMessage: { type: String, default: "" },
  /** IQ generation UI while state is validating (task / checkpoint context check). */
  iqGenerationStatus: {
    type: String,
    default: "connecting",
    validator: (v) => ["connecting", "streaming", "done", "stalled"].includes(v),
  },
  iqTraceEntries: {
    type: Array,
    default: () => [],
  },
  iqGenerationInProgress: {
    type: Boolean,
    default: true,
  },
  /** When false (tasks upload), hide Connecting/Streaming/Done/Stalled pills only. */
  showIqStatusPills: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "input",
  "select",
  "remove",
  "submit",
  "go-back",
  "close",
  "done",
  "abort",
]);

const fileInput = ref(null);
const isDragging = ref(false);
const internalState = ref(props.state);
const internalFile = ref(props.file);

watch(
  () => props.state,
  (v) => {
    internalState.value = v;
  }
);
watch(
  () => props.file,
  (v) => {
    internalFile.value = v;
  }
);

const currentState = computed(() => internalState.value);

const PROCESSING_TITLE_STATES = ["processing", "validating", "not-related"];

const showAbort = computed(() =>
  ["processing", "validating"].includes(currentState.value)
);

const displayTitle = computed(() => {
  if (currentState.value === "related") return "";
  if (PROCESSING_TITLE_STATES.includes(currentState.value)) return "Processing Video";
  return props.title;
});

const processingText = computed(() => "This usually takes a few minutes.");

const dialogLabelledBy = computed(() =>
  currentState.value === "related" ? "upload-video-wizard-alert-title" : "upload-video-wizard-title"
);

const clampedProgress = computed(() =>
  Math.max(0, Math.min(100, Math.round(Number(props.progress) || 0)))
);

const displayFileName = computed(() => internalFile.value?.name || "video.mp4");
const displayFileSize = computed(() => formatBytes(internalFile.value?.size));

const isBusyState = computed(() =>
  ["progress", "processing", "validating"].includes(currentState.value)
);

const showActions = computed(() => currentState.value !== "progress");
const showGoBack = computed(
  () => currentState.value !== "related" && currentState.value !== "not-related"
);

const relatedTitle = computed(
  () => "The video was successfully uploaded, processed and validated"
);
const relatedText = computed(
  () => props.resultMessage || "The task to upload the video is now completed"
);

const alertTitle = computed(() => {
  if (currentState.value === "too-long") return "This video is longer than 5 minutes";
  if (currentState.value === "not-related") {
    return "There was a problem with the video";
  }
  return "Video failed to upload";
});
const alertText = computed(() => {
  if (currentState.value === "too-long") return "Trim it to 5:00 or less and try again.";
  if (currentState.value === "not-related") {
    return props.resultMessage || "Upload a different video and try again.";
  }
  return "Please try again";
});

const primaryLabel = computed(() => {
  if (currentState.value === "related") return props.doneLabel;
  if (
    currentState.value === "error" ||
    currentState.value === "too-long" ||
    currentState.value === "not-related"
  ) {
    return props.retryLabel;
  }
  return props.submitLabel;
});

const primaryDisabled = computed(() => {
  if (currentState.value === "upload") return true;
  if (
    currentState.value === "error" ||
    currentState.value === "too-long" ||
    currentState.value === "not-related" ||
    currentState.value === "related"
  ) {
    return false;
  }
  return !internalFile.value;
});

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

function openFilePicker() {
  fileInput.value && fileInput.value.click();
}

function onFileChange(e) {
  const file = e.target.files && e.target.files[0];
  if (file) handleSelectedFile(file);
  e.target.value = "";
}

function onDrop(e) {
  isDragging.value = false;
  const file = e.dataTransfer.files && e.dataTransfer.files[0];
  if (file) handleSelectedFile(file);
}

function handleSelectedFile(file) {
  checkDuration(file)
    .then((duration) => {
      if (duration > MAX_DURATION_SECONDS) {
        internalState.value = "too-long";
        emit("select", { file, duration, tooLong: true });
        return;
      }
      internalFile.value = file;
      internalState.value = "selected";
      emit("select", { file, duration, tooLong: false });
    })
    .catch(() => {
      internalFile.value = file;
      internalState.value = "selected";
      emit("select", { file, duration: null, tooLong: false });
    });
}

function checkDuration(file) {
  return new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(video.duration);
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("metadata error"));
      };
      video.src = url;
    } catch (e) {
      reject(e);
    }
  });
}

function onRemove() {
  internalFile.value = null;
  internalState.value = "upload";
  emit("remove");
}

function onGoBack() {
  if (isBusyState.value) return;
  emit("go-back");
  emit("input", false);
}

function onClose() {
  if (currentState.value === "progress" || currentState.value === "validating") return;
  emit("close");
  emit("input", false);
}

function onAbort() {
  emit("abort");
  emit("input", false);
}

function onSubmit() {
  if (currentState.value === "related") {
    emit("done");
    emit("input", false);
    return;
  }
  if (
    currentState.value === "error" ||
    currentState.value === "too-long" ||
    currentState.value === "not-related"
  ) {
    internalFile.value = null;
    internalState.value = "upload";
    openFilePicker();
    return;
  }
  if (!internalFile.value) return;
  emit("submit", internalFile.value);
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) onClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.upload-video-wizard-enter-active,
.upload-video-wizard-leave-active {
  transition: opacity 180ms ease;
}

.upload-video-wizard-enter-from,
.upload-video-wizard-leave-to {
  opacity: 0;
}

.upload-video-wizard-enter-active .uvw,
.upload-video-wizard-leave-active .uvw {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.upload-video-wizard-enter-from .uvw,
.upload-video-wizard-leave-to .uvw {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .upload-video-wizard-enter-active,
  .upload-video-wizard-leave-active,
  .upload-video-wizard-enter-active .uvw,
  .upload-video-wizard-leave-active .uvw {
    transition: none !important;
  }
}

.upload-video-wizard__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: "Inter", sans-serif;
}

.uvw {
  position: relative;
  width: 546px;
  max-width: 100%;
  background: #ffffff;
  background-image: url("@components/assets/Background_pattern_decorative_new.svg");
  background-repeat: no-repeat;
  background-position: top left;
  border-radius: 12px;
  box-shadow:
    0px 20px 24px -4px rgba(16, 24, 40, 0.08),
    0px 8px 8px -4px rgba(16, 24, 40, 0.03);
  overflow: hidden;
  padding-bottom: 24px;
}

.uvw__header {
  position: relative;
  z-index: 1;
  padding: 24px 24px 0 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 20px;
}

.uvw__featured-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow: 0px 1px 1px rgba(16, 24, 40, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.uvw__featured-icon > * {
  display: flex;
  align-items: center;
  justify-content: center;
}

.uvw__title {
  width: 100%;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
  text-align: center;
}

.uvw__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: #98a2b3;
}
.uvw__close:hover {
  background: #f9fafb;
}
.uvw__close:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.uvw__content {
  position: relative;
  z-index: 1;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: visible;
}

/* Dropzone */
.uvw__dropzone {
  width: 100%;
  padding: 16px 24px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background-color 120ms ease;
}
.uvw__dropzone:hover,
.uvw__dropzone--drag {
  border-color: #7f56d9;
  background: #fafaff;
}
.uvw__dropzone-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
}
.uvw__dropzone-icon-img {
  display: block;
  width: 20px;
  height: 20px;
}
.uvw__dropzone-text {
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}
.uvw__dropzone-action {
  color: #6941c6;
  font-weight: 600;
}
.uvw__dropzone-or {
  color: #475467;
  font-weight: 400;
}
.uvw__dropzone-help {
  font-size: 12px;
  line-height: 18px;
  color: #475467;
  text-align: center;
}

/* File row */
.uvw__file-row {
  position: relative;
  width: 100%;
  padding: 16px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #ffffff;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.uvw__file-icon {
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}
.uvw__file-icon-img {
  display: block;
  width: 100%;
  height: 100%;
}
.uvw__file-badge {
  position: absolute;
  left: 2px;
  bottom: 7px;
  background: #155eef;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 3px;
  border-radius: 2px;
  letter-spacing: 0.2px;
}
.uvw__file-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.uvw__file-name {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.uvw__file-size {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #475467;
}
.uvw__progress {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.uvw__progress-track {
  flex: 1;
  height: 8px;
  background: #eaecf0;
  border-radius: 9999px;
  overflow: hidden;
}
.uvw__progress-fill {
  height: 100%;
  background: #7f56d9;
  border-radius: 9999px;
  transition: width 200ms ease;
}
.uvw__progress-text {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #344054;
}
.uvw__trash {
  position: absolute;
  top: 7px;
  right: 7px;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.uvw__trash:hover {
  background: #f9fafb;
}
.uvw__trash-icon {
  display: block;
  width: 20px;
  height: 20px;
}

/* Alert (error / too-long) */
.uvw__alert {
  width: 100%;
  padding: 16px 24px;
  border: 1px solid #f04438;
  border-radius: 12px;
  background: #fef2f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.uvw__alert-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #f04438;
  background: #fee4e2;
  box-shadow: 0px 1px 1px rgba(16, 24, 40, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}
.uvw__alert-title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #b42318;
  text-align: center;
}
.uvw__alert-text {
  margin-top: -8px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: #b42318;
  text-align: center;
}

/* Status alert (match / mismatch) */
.uvw__status-alert {
  width: 100%;
  padding: 16px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0px 1px 1px rgba(16, 24, 40, 0.05);
  display: flex;
  align-items: flex-start;
  gap: 16px;
  overflow: visible;
}
.uvw__status-alert-icon {
  position: relative;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 9999px;
}
.uvw__status-alert-icon-img {
  display: block;
  width: 20px;
  height: 20px;
}
.uvw__status-alert-ring {
  position: absolute;
  border-style: solid;
  border-radius: 9999px;
  pointer-events: none;
  box-sizing: border-box;
}
.uvw__status-alert-icon--error .uvw__status-alert-ring--inner {
  inset: -20%;
  border-width: 2px;
  border-color: #d92d20;
  opacity: 0.3;
}
.uvw__status-alert-icon--error .uvw__status-alert-ring--outer {
  inset: -45%;
  border-width: 2px;
  border-color: #d92d20;
  opacity: 0.1;
}
.uvw__status-alert-icon--success .uvw__status-alert-ring--inner {
  inset: -16.67%;
  border-width: 1.667px;
  border-color: #079455;
  opacity: 0.3;
}
.uvw__status-alert-icon--success .uvw__status-alert-ring--outer {
  inset: -37.5%;
  border-width: 1.667px;
  border-color: #079455;
  opacity: 0.1;
}
.uvw__status-alert-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.uvw__status-alert-title {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #344054;
}
.uvw__status-alert-text {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #475467;
}

/* Processing (Cloudflare status) */
.uvw__processing {
  width: 100%;
  padding: 16px;
  border: 1px dashed #eaecf0;
  border-radius: 8px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.uvw__processing-loader {
  width: 48px;
  height: 48px;
}
.uvw__processing-text {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #344054;
  text-align: center;
}

/* Validating (video_to_components) */
.uvw__thinking {
  width: 100%;
}
.uvw__actions--stretch {
  justify-content: stretch;
}
.uvw__actions--stretch :deep(.standard-button) {
  width: 100%;
}
.uvw__actions--equal {
  justify-content: stretch;
}
.uvw__actions--equal :deep(.standard-button) {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  flex-shrink: 1;
}

/* Footer */
.uvw__actions {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 32px 24px 0 24px;
}

.uvw__file-input {
  display: none;
}
</style>
